import { getStoredConsent, clearStoredConsent } from './consent';
import { getRegionState, logVisitorRegion } from './region';
import { showBanner, showPreferences, showCookieIcon, openCookieSettings } from './banner';
import { initGating, installEarlyConsentDefaults } from './gating';
import { sendCookieBannerAnalytics } from './analyticsSend';

// Same defaults as admin DEFAULT_COOKIE_BANNER_SETTINGS (store default design).
const DEFAULT_CONFIG = {
  layout: 'banner',
  position: 'bottom',
  color_scheme: 'light',
  bg_color: '#ffffff',
  text_color: '#222222',
  font_family: '',
  font_size: 16,
  button_accept_bg: '#2563eb',
  button_accept_text: '#ffffff',
  button_reject_bg: 'transparent',
  button_reject_text: '#222222',
  button_prefs_text: '#222222',
  button_prefs_border: '#6b7280',
  consent_model: 'accept_reject_preferences',
  consent_type: 'explicit',
  force_banner_interaction: false,
  google_consent_mode: false,
  microsoft_consent_mode: false,
  global_privacy_control: false,
  banner_text:
    'We use cookies to enhance your experience, analyze traffic, and personalize content. Some cookies are essential, while others help us understand how you interact with our site. You can manage your preferences at any time.',
  privacy_policy_text: 'Privacy Policy',
  privacy_policy_url: '',
  imprint_link_enabled: false,
  imprint_text: 'Imprint',
  imprint_url: '',
  preferences_button_text: 'Preferences',
  reject_button_text: 'Reject',
  accept_button_text: 'Accept',
  pref_popup_title: 'Select the Types of Cookies You Accept',
  pref_popup_description:
    'We use cookies to enhance your experience, serve personalized ads, and analyze traffic. View cookie details by category below.',
  necessary_title: 'Necessary',
  necessary_description:
    'Essential for the website to function properly, these cookies enable core features such as security, page navigation, and access to secure areas. They cannot be disabled.',
  statistics_title: 'Statistics',
  statistics_description:
    'By collecting anonymous data, these cookies help us understand how visitors interact with the website, allowing us to improve performance and user experience.',
  marketing_title: 'Marketing',
  marketing_description:
    'These cookies track your activity across websites to deliver personalized ads and relevant content. They also help measure the effectiveness of our marketing campaigns.',
  preferences_cat_title: 'Preferences',
  preferences_cat_description:
    'These cookies remember your choices - such as language preferences or login details - to provide a more personalized experience.',
  save_choice_button_text: 'Save my choice',
  reject_all_button_text: 'Reject All',
  accept_all_button_text: 'Accept All',
  cookie_settings_title: 'Your cookie settings',
  withdraw_consent_text: 'Withdraw consent',
  change_consent_text: 'Change consent',
  cookie_icon_enabled: false,
  cookie_icon_display: 'icon',
  cookie_icon_reopen_text: 'Manage cookie',
  cookie_icon_position: 'bottom-left',
  cookie_icon_style: 'cookie-vector',
  cookie_icon_bg_color: '#2563eb',
  cookie_icon_color: '#ffffff',
  cookie_icon_size: 'medium',
  cookie_icon_radius: 50,
  cookie_icon_shadow: 100,
  cookie_icon_bg_enabled: false,
  selected_regions: ['worldwide'],
};

let lastConfig = null;

const SETTINGS_TRIGGER_SELECTOR = [
  '[data-acb-cookie-settings]',
  '.acb-cookie-settings-trigger',
  '#acb-cookie-settings-trigger',
  'a[href$="#cookie-settings"]',
].join(',');

const SETTINGS_MESSAGE_TYPE = 'acb:open-cookie-settings';

let settingsTriggersBound = false;
let pendingSettingsOpen = false;

function triggerSelector() {
  const custom = lastConfig && lastConfig.cookie_settings_trigger_selector;
  return typeof custom === 'string' && custom.trim()
    ? `${SETTINGS_TRIGGER_SELECTOR},${custom.trim()}`
    : SETTINGS_TRIGGER_SELECTOR;
}

function findTrigger(target) {
  if (!target || typeof target.closest !== 'function') return null;
  try {
    return target.closest(triggerSelector());
  } catch (_) {
    return target.closest(SETTINGS_TRIGGER_SELECTOR);
  }
}

function openSettingsPanel() {
  if (!lastConfig) {
    pendingSettingsOpen = true;
    return false;
  }
  return openCookieSettings(lastConfig);
}

function bindCookieSettingsTriggers() {
  if (settingsTriggersBound) return;
  settingsTriggersBound = true;

  document.addEventListener('click', event => {
    const trigger = findTrigger(event.target);
    if (!trigger) return;
    event.preventDefault();
    openSettingsPanel();
  });

  document.addEventListener('keydown', event => {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    const trigger = findTrigger(event.target);
    if (!trigger) return;
    event.preventDefault();
    openSettingsPanel();
  });

  window.addEventListener('message', event => {
    const data = event && event.data;
    const type = typeof data === 'string' ? data : data && data.type;
    if (type !== SETTINGS_MESSAGE_TYPE) return;
    openSettingsPanel();
  });
}

function shouldActivate(config) {
  const status = config.status || 'enabled';
  if (status === 'disabled') return false;
  return true;
}

function exposePublicAPI() {
  if (window.AccessProCookieBanner) return;
  window.AccessProCookieBanner = {
    getConsent() { return getStoredConsent(); },
    open() {
      if (!lastConfig) return false;
      showPreferences(lastConfig);
      return true;
    },
    openSettings() {
      return openSettingsPanel();
    },
    bind(target) {
      const el =
        typeof target === 'string' ? document.querySelector(target) : target;
      if (!el || typeof el.addEventListener !== 'function') return false;
      el.addEventListener('click', event => {
        event.preventDefault();
        openSettingsPanel();
      });
      return true;
    },
    withdraw() {
      clearStoredConsent();
      if (lastConfig) {
        showBanner(lastConfig);
        showCookieIcon(lastConfig);
      } else location.reload();
    },
  };
}

function initBanner(config) {
  lastConfig = config;

  if (pendingSettingsOpen) {
    pendingSettingsOpen = false;
    openCookieSettings(config);
  }

  if (!shouldActivate(config)) return;

  const regionPromise = getRegionState(config);

  const stored = getStoredConsent();

  // Expose saved settings so gating.js can read integration toggles
  // (google_consent_mode, microsoft_consent_mode, global_privacy_control, etc.).
  window.accessProCookieBannerSettings = config;

  // Always set up gating + Consent Mode v2 defaults — even when consent already
  // exists — so previously-granted scripts are re-activated on every page load.
  try {
    initGating(stored);
  } catch (_) {}

  regionPromise.then(state => {
    logVisitorRegion('site open', state);
    if (stored) {
      if (state.inRegion) showCookieIcon(config);
      return;
    }
    if (state.inRegion) {
      showBanner(config);
      showCookieIcon(config);
      sendCookieBannerAnalytics('impression');
    }
  });
}

function isValidConfig(config) {
  return (
    config !== null &&
    config !== undefined &&
    typeof config === 'object' &&
    !Array.isArray(config) &&
    Boolean(config.layout || config.consent_model || config.banner_text)
  );
}

function resolveSettingsUrl() {
  if (window.accessProCookieBannerSettingsUrl) {
    return window.accessProCookieBannerSettingsUrl;
  }

  try {
    const scripts = document.getElementsByTagName('script');
    for (let i = scripts.length - 1; i >= 0; i -= 1) {
      const src = scripts[i].src || '';
      if (!src.includes('cookie-banner.js')) continue;
      const url = new URL(src);
      const storeHash = url.searchParams.get('instance');
      if (!storeHash) continue;
      return `${url.origin}/api/cookie-banner/settings?store_hash=${encodeURIComponent(storeHash)}`;
    }
  } catch (_) {}

  return null;
}

export default function bootstrap() {
  exposePublicAPI();
  bindCookieSettingsTriggers();

  // Seed proxy from this script's origin so analytics works without main.js.
  try {
    const scripts = document.getElementsByTagName('script');
    for (let i = scripts.length - 1; i >= 0; i -= 1) {
      const src = scripts[i].src || '';
      if (!src.includes('cookie-banner.js')) continue;
      const url = new URL(src);
      if (!window.accessProProxyHandle) {
        window.accessProProxyHandle = `${url.origin}/api/v1`;
      }
      break;
    }
  } catch (_) {}

  const injected = window.accessProCookieBannerSettings;
  if (isValidConfig(injected)) {
    initBanner(injected);
    return;
  }

  const url = resolveSettingsUrl();
  if (url) {
    fetch(url)
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (data && data.success && data.enabled && isValidConfig(data.data)) {
          initBanner({ ...DEFAULT_CONFIG, ...data.data, status: data.status });
          return;
        }
        initBanner(DEFAULT_CONFIG);
      })
      .catch(() => {
        initBanner(DEFAULT_CONFIG);
      });
    return;
  }

  initBanner(DEFAULT_CONFIG);
}
